import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useHistory } from 'react-router-dom';
import { isSafari } from 'react-device-detect';

import { ITheme } from '@williamhill/rosi-ui-shared/src/themes';
import NoTicketsComponent from './no-tickets-component';
import TicketComponent from './ticket';
import { useBets } from './use-bets';
import { SubmissionModalContent } from '../components/modal';
import useModal from '../use-modal';
import { BETS_TAB_QUERY, TabQueryParameters, useBetsNavigation } from '../navigation';
import { withRootTestId } from '@williamhill/rosi-ui-shared/src/lib';
import { fillHeightForMobile } from '@williamhill/rosi-ui-shared/src/global-style';
import useBetsContext from './use-bets-context';
import Header from '../components/header';
import { Block, Text, DisplayHeading } from '@williamhill/rosi-ui-shared/src/components/typography/Typography';

const withTestId = withRootTestId('qa-bet-tracker-bets');
const selectionHighlightTimeout = 5000;

export default function BetsComponent() {
  const history = useHistory();
  const search = new URLSearchParams(history.location.search);

  const selectedTab: TabQueryParameters = search.get(BETS_TAB_QUERY) === 'settled' ? 'settled' : 'open';
  const { openTickets, settledTickets, clearAll } = useBets();
  const tickets = selectedTab === 'open' ? openTickets : settledTickets;
  const { show: showModal, close: closeModal } = useModal();
  const { goToSettledBets, goToOpenBets } = useBetsNavigation();
  const { context, clearContext } = useBetsContext();

  useEffect(() => {
    if (context) {
      const timeout = setTimeout(() => clearContext(), selectionHighlightTimeout);
      return () => {
        clearTimeout(timeout);
      };
    }
    return () => {};
  }, [context]);

  useEffect(() => {
    return clearContext;
  }, []);

  return (
    <Container>
      <Header>
        <Heading as="h1" size="lg" weight="bold">
          My Bets
        </Heading>
      </Header>
      <SelectionAndAction>
        <TabSelection>
          <Tab
            data-testid={withTestId('open-tab')}
            selected={selectedTab === 'open'}
            onClick={handleTabSelection('open')}
            as="li"
            size="lg"
            weight="medium">
            Open Bets
          </Tab>
          <Tab
            data-testid={withTestId('settled-tab')}
            selected={selectedTab === 'settled'}
            onClick={handleTabSelection('settled')}
            as="li"
            size="lg"
            weight="regular">
            Settled Bets
          </Tab>
        </TabSelection>
        <ClearAll weight="regular" data-testid={withTestId('clear-all')} onClick={handleShowClearAllConfirmation}>
          Clear All
        </ClearAll>
      </SelectionAndAction>
      <TabContent>
        {tickets.length ? (
          <>
            <main>
              {tickets.map(t => (
                <TicketComponent
                  key={t.retailTicketId}
                  isSelected={t.retailTicketId === context?.selectedTicketId}
                  {...t}
                />
              ))}
            </main>
            <footer>
              <WarningBox>
                <Text weight="bold">Important:</Text> This is not a replacement for your printed paper ticket. To redeem
                any and all winnings please keep your ticket safe.
              </WarningBox>
            </footer>
          </>
        ) : (
          <NoTicketsComponent {...{ selectedTab }} />
        )}
      </TabContent>
    </Container>
  );

  function handleTabSelection(tab: TabQueryParameters) {
    return function () {
      (tab === 'open' ? goToOpenBets : goToSettledBets)();
    };
  }

  function handleShowClearAllConfirmation() {
    showModal(
      <SubmissionModalContent
        title="Clear All Bets?"
        onSubmit={handleClearAllSubmit}
        submitTitle="Clear All Bets?"
        cancelTitle="Close"
        onCancel={closeModal}
        baseTestId={withTestId()}
      />
    );
  }

  function handleClearAllSubmit() {
    clearAll();
    closeModal();
  }
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  ${isSafari
    ? fillHeightForMobile
    : css`
        min-height: 100%;
      `}
`;

const Heading = styled(DisplayHeading)`
  text-transform: uppercase;
  position: relative;
  text-align: center;
  margin: 0;
  line-height: 1;
  width: 100%;
`;

const SelectionAndAction = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  margin: 3.7rem 0 0 0;
`;

const TabSelection = styled.ul`
  display: flex;
  padding: 0;
  margin: 0;
`;

const ClearAll = styled(Block)`
  cursor: pointer;
  align-self: flex-start;
  margin-left: auto;
  margin-right: 1.6rem;
  color: ${({ theme }) => theme.colors.modals.error};
`;

const selectedTab = (theme: ITheme) => css`
  color: ${theme.colors.brand.foreground};
  border-bottom: solid 4px ${theme.components.mobile.tab.selection};
  padding-bottom: 1rem;
  font-weight: 600;
`;

const Tab = styled(Text)<{ selected?: boolean }>`
  width: 12rem;
  text-align: center;
  list-style-type: none;
  line-height: 1;
  color: ${({ theme }) => theme.colors.brand.quaternary};
  ${({ theme, selected }) => (selected ? selectedTab(theme) : '')};
`;

const TabContent = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  background-color: ${({ theme }) => theme.colors.grey100};
  padding: 1rem 0;

  & > main {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
`;

const WarningBox = styled.div`
  background: ${({ theme }) => `${theme.colors.white}66`};
  margin: 2.4rem 3.5rem;
  padding: 1.7rem;
  text-align: center;
`;
